import React, { useEffect, useState, useRef, useContext, useCallback } from 'react'
import useAxios from 'hooks/useAxios'
import { Dropdown, Input, Form, FormField, Checkbox, Grid } from 'semantic-ui-react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getSurveyTokenFromId } from 'reducers'
import { Loader } from 'shared/components/Loader'
import WebEmbedAudienceRule from './WebEmbedAudienceRule'
import * as R from 'ramda'
import { toast } from 'libs/toast'
import { surveySettingContext } from 'pages/SurveySettings/context'
import { transformAudienceRuleToUiState } from 'domains/email'
import { ImportCustomerContainer } from 'organisms/ImportCustomerButton/container'
import FilterGroup from 'shared/components/FilterGroup'
import FilterInput from 'components/FilterInput'
import { convertStateAudienceRule } from 'organisms/AudienceRuleForm'
import { CustomerListWidget } from 'organisms/CustomerListWidget/container'
import { csvDownload } from 'libs/csv-download'
import { TooltipLabel } from 'components/TooltipLabel'

export default function WebEmbedSurveyPublish() {
  const { surveyId } = useParams()
  const surveyToken = useSurveyToken()
  const [audienceRule, setAudienceRule] = useState([])
  const [webEmbedSetting, setWebEmbedSetting] = useState(null)
  const [isCSVDownloading, setIsCSVDownloading] = useState(false)
  const [visibilities, setVisibilities] = useState([])
  const [connectorId, setConnectorId] = useState(undefined)
  // PET-2084: track dirty in a LOCAL flag from the change handlers and sync to
  // the shared context in an effect (not synchronously inside onChange, which
  // re-renders controlled inputs and breaks editing — see generic-embed).
  const { setContext: setSurveySettingContext } = useContext(surveySettingContext)
  const [isFormDirty, setIsFormDirty] = useState(false)
  useEffect(() => {
    setSurveySettingContext({ isSurveyBuilderDirty: isFormDirty })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDirty])
  // Wrapper for the web_embed setting edits (the functional-updater call sites,
  // NOT the seed/reset ones which pass a value directly) so each toggle marks dirty.
  const updateWebEmbedSetting = (updater) => {
    setWebEmbedSetting(updater)
    setIsFormDirty(true)
  }

  const getWebEmbedSettingApi = useAxios({
    url: `survey/survey/${surveyToken}/survey-settings/`,
    skip: !surveyToken,
  })

  const saveWebEmbedSettingApi = useAxios({
    method: 'patch',
    url: `survey/survey/${surveyToken}/survey-settings/`,
    skip: true,
  })

  const getAudienceRulesApi = useAxios({
    url: `survey/survey/${surveyToken}/survey-sending-rules/`,
    skip: !surveyToken,
  })

  const saveAudienceRulesApi = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/survey-sending-rules/`,
    skip: true,
  })

  const customersMatchingAudienceRulesApi = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/audience/`,
    skip: true,
  })

  const advancedFilterFields = useAxios({
    url: 'reports/preload-advanced-filters/',
  })

  const getSurveyDetail = useAxios({
    url: `account/survey/${surveyId}/`,
    skip: !surveyId,
  })

  const saveConnector = useAxios({
    method: 'patch',
    url: `account/survey/${surveyId}/`,
    skip: true,
  })

  const { data: connectors, loading: loadingConnectors } = useAxios({
    url: `cyclr/account/connectors/authenticated/`,
  })

  const connectorOptions =
    connectors?.results?.map(({ id, name }) => ({
      key: id,
      text: name,
      value: id,
    })) || []

  const buttonRef = useRef()
  useSetCurrentSaveFunction(buttonRef)
  useSetHeaderSaveButtonLoading(saveWebEmbedSettingApi.loading, !getWebEmbedSettingApi.data)

  function useSetCurrentSaveFunction(buttonRef) {
    const { setContext } = useContext(surveySettingContext)

    useEffect(() => {
      function saveApiOptions() {
        if (buttonRef.current) {
          buttonRef.current.click()
        }
      }

      setContext({
        currentSaveFunction: saveApiOptions,
      })
    }, [buttonRef, setContext])
  }

  const getAudienceRulesState = useCallback(() => {
    if (R.prop('audience_rules', getAudienceRulesApi.data) && advancedFilterFields.data) {
      const audienceRulesFields = getAudienceRulesFields(advancedFilterFields.data)
      const audienceRulesState = transformAudienceRuleToUiState(
        getAudienceRulesApi.data,
        audienceRulesFields
      )
      return audienceRulesState
    }
  }, [advancedFilterFields.data, getAudienceRulesApi.data])

  function downloadAudienceRulesCSV(event) {
    event.preventDefault()
    setIsCSVDownloading(true)
    const audienceRulePayload = audienceRule.filter((rule) => !rule.extra.key.includes('ticket.'))
    customersMatchingAudienceRulesApi
      .fetch({
        data: {
          audience_rules: audienceRulePayload,
          show_list: true,
          all: true,
        },
      })
      .then((response) => {
        const customers = response.data.customers.map((row) => ({
          Name: row.name,
          Email: row.email,
          Company: row.company || '',
        }))

        const fileName = 'matching_audience_rules.csv'
        const header = ['Name', 'Email', 'Company']
        csvDownload(customers, fileName, header, { userInputFields: header })
        setIsCSVDownloading(false)
      })
  }

  useEffect(() => {
    if (surveyToken && getWebEmbedSettingApi.data) {
      const webEmbedSettings = getWebEmbedSettingApi.data
      setWebEmbedSetting(webEmbedSettings)
    }

    if (
      surveyToken &&
      R.prop('audience_rules', getAudienceRulesApi.data) &&
      advancedFilterFields.data
    ) {
      const audienceRulesFields = getAudienceRulesFields(advancedFilterFields.data)
      const audienceRulesState = transformAudienceRuleToUiState(
        getAudienceRulesApi.data,
        audienceRulesFields
      )
      const audienceRuleCondition = audienceRulesState.map((rule) => {
        return convertStateAudienceRule(rule)
      })
      setAudienceRule(audienceRuleCondition)
    }

    if (getSurveyDetail.data) {
      setConnectorId(getSurveyDetail.data?.connector?.id)
    }
  }, [
    surveyToken,
    getWebEmbedSettingApi.data,
    getAudienceRulesApi.data,
    advancedFilterFields.data,
    getSurveyDetail.data,
  ])

  useEffect(() => {
    if (audienceRule && surveyToken) {
      const audienceRulePayload = audienceRule.filter((rule) => {
        if (rule.extra) {
          return !rule.extra.key.includes('ticket.')
        } else {
          return rule
        }
      })

      const getCustomersMatchingAudienceRules = customersMatchingAudienceRulesApi.fetch
      getCustomersMatchingAudienceRules({
        data: {
          audience_rules: audienceRulePayload,
          show_list: true,
        },
      })
    }
  }, [audienceRule, customersMatchingAudienceRulesApi.fetch, surveyToken])

  function useSetHeaderSaveButtonLoading(loading, isDisabled) {
    const { setContext } = useContext(surveySettingContext)
    useEffect(() => {
      setContext({
        isSubmitting: loading,
        isDisabled: isDisabled,
      })

      return () => {
        setContext({
          isSubmitting: false,
          isDisabled: false,
        })
      }
    }, [loading, setContext, isDisabled])
  }

  function useSurveyToken() {
    return useSelector((state) => getSurveyTokenFromId(state, surveyId))
  }

  function handleSubmit(e) {
    e.preventDefault()
    const audienceRuleData = {
      survey_id: surveyId,
      audience_rules: R.isEmpty(audienceRule) ? [] : convertTicketAttributeFormat(audienceRule),
    }

    const webEmbedSettingData = {
      web_embed: webEmbedSetting.web_embed,
      web_embed_visibility: visibilities,
    }

    Promise.all([
      saveWebEmbedSettingApi.fetch({ data: webEmbedSettingData }),
      saveAudienceRulesApi.fetch({ data: audienceRuleData }),
      saveConnector.fetch({ data: { connector: connectorId || null } }),
    ])
      .then((response) => {
        const [webEmbedResponse, audienceRuleResponse] = response

        setWebEmbedSetting(webEmbedResponse.data)
        const audienceRulesFields = getAudienceRulesFields(advancedFilterFields.data)
        const audienceRulesState = transformAudienceRuleToUiState(
          audienceRuleResponse.data,
          audienceRulesFields
        )
        const audienceRuleCondition = audienceRulesState.map((rule) => {
          return convertStateAudienceRule(rule)
        })
        setAudienceRule(audienceRuleCondition)
        setIsFormDirty(false)
        toast.success('Web-embed settings were successfully updated.')
      })
      .catch(() =>
        toast.error(
          'Failed to save web-embed settings. Please try again or contact Simplesat support.'
        )
      )
  }

  const handleVisibilitiesChange = useCallback(
    (filterData) => {
      const newVisibilities = filterData.rules.map((rule) => {
        const id = R.pathOr(null, ['extra', 'web_embed_visibility_id'], rule)
        const url = R.pathOr(null, ['filters', 0, 'value'], rule)
        const comparison = rule.comparison

        return R.reject(R.equals(null))({ id, comparison, url })
      })
      setVisibilities(newVisibilities)
      setIsFormDirty(true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setVisibilities]
  )

  function mapVisibilitiesToFilterValue(visibilities) {
    return visibilities.map((visibility) => ({
      field: 'url',
      label: 'Current page URL',
      icon: 'far fa-link',
      extra: { web_embed_visibility_id: visibility.id },
      comparison: visibility.comparison,
      filters: [
        {
          value: visibility.url,
        },
      ],
    }))
  }

  if (
    !webEmbedSetting ||
    !getAudienceRulesApi.data ||
    !advancedFilterFields.data ||
    getWebEmbedSettingApi.loading
  ) {
    return <Loader withContainer containerHeight={150} />
  }

  const allFields = R.map(
    (field) => ({
      label: field.label,
      value: field.field,
      key: field.key,
      isCoreField: field.isCoreField,
      field_type: field.field_type,
    }),
    getAudienceRulesFields(advancedFilterFields.data)
  )
  return (
    <React.Fragment>
      <div className="ui stackable grid">
        <div className="fourteen wide column">
          <h2>Web-embed survey</h2>
          <p>
            Embed surveys directly in your app or webpage, enabling customers to provide feedback
            without leaving your site.
          </p>
          <p>
            Adjust the settings as needed, then click “Get embed code” on the top right. Simply copy
            and insert the provided code into your website.{' '}
          </p>

          <a
            href="https://help.simplesat.io/en/articles/8094151-web-embed-surveys"
            target="_blank"
            rel="noreferrer"
          >
            <i className="icon far fa-book" />
            Learn more about web-embed surveys
          </a>
          <h3>Sending audience</h3>
          <p>
            Create an audience to determine which customers see the survey. The rules will apply to
            customers in your account and fields passed via the embed code. Leaving rules blank
            targets all customers.
          </p>

          <div className="mb4 mt4">
            <ImportCustomerContainer />
          </div>
        </div>
      </div>
      <Form onSubmit={handleSubmit}>
        <div className="ui stackable two column grid">
          <div className="column">
            <WebEmbedAudienceRule
              fields={allFields}
              setAudienceRule={(value) => {
                setAudienceRule(value)
                setIsFormDirty(true)
              }}
              getAudienceRulesState={getAudienceRulesState}
            />
          </div>
          <div className="column">
            <CustomerListWidget
              loading={customersMatchingAudienceRulesApi.loading && !isCSVDownloading}
              customers={customersMatchingAudienceRulesApi.data}
              audienceRules={audienceRule}
              downloadAudienceRulesCSV={downloadAudienceRulesCSV}
              isDownloading={customersMatchingAudienceRulesApi.loading}
            />
          </div>
        </div>
        <FormField>
          <h3>
            <TooltipLabel
              label="Visibility"
              descriptionTitle="Visibility"
              descriptionContent="Define which pages the survey will appear or be hidden."
            />
          </h3>
          <FilterGroup
            key={JSON.stringify(webEmbedSetting.web_embed_visibility)}
            allowKeepingSelectedOption
            condition="and"
            onChange={handleVisibilitiesChange}
            defaultValue={mapVisibilitiesToFilterValue(webEmbedSetting.web_embed_visibility)}
            options={[
              {
                field: 'url',
                label: 'Current page URL',
                icon: 'far fa-link',
                comparison: 'is', // default comparison
              },
            ]}
            comparisons={[
              { label: 'is', value: 'is', inputComponent: FilterInput },
              { label: 'is not', value: 'is_not', inputComponent: FilterInput },
              { label: 'contains', value: 'contains', inputComponent: FilterInput },
              {
                label: 'does not contain',
                value: 'does_not_contain',
                inputComponent: FilterInput,
              },
            ]}
          />
        </FormField>

        <FormField>
          <Grid stackable columns={3}>
            <Grid.Column>
              <h3>
                <TooltipLabel
                  label="Delay"
                  descriptionTitle="Delay"
                  descriptionContent="Determine the number of seconds before the survey appears on the page."
                />
              </h3>
              <div className="flex flex-row field" style={{ alignItems: 'center' }}>
                <Input
                  type="number"
                  required
                  label="seconds"
                  labelPosition="right"
                  min={0}
                  max={9999}
                  value={webEmbedSetting.web_embed.time_on_page_second}
                  onChange={(e) => {
                    if (e.target.valueAsNumber >= 0) {
                      updateWebEmbedSetting((embed) =>
                        R.assocPath(
                          ['web_embed', 'time_on_page_second'],
                          e.target.valueAsNumber,
                          embed
                        )
                      )
                    }
                  }}
                />
              </div>
            </Grid.Column>
          </Grid>
        </FormField>

        <FormField>
          <h3>
            <TooltipLabel
              label="Frequency"
              descriptionTitle="Frequency"
              descriptionContent={
                <div>
                  <p>
                    Set how often the survey appears for customers. They'll see the survey initially
                    until they respond or close it.
                  </p>
                  <ul>
                    <li>
                      <strong>One time</strong> - shows the survey once.
                    </li>
                    <li>
                      <strong>Persistent</strong> - displays it each time the embed code loads.
                    </li>
                    <li>
                      <strong>Ongoing</strong> - repeats it after a set timeframe.
                    </li>
                  </ul>
                </div>
              }
            />
          </h3>
          <Form.Checkbox
            checked={webEmbedSetting.web_embed.frequency_type === 'oneoff'}
            onChange={() =>
              updateWebEmbedSetting((embed) =>
                R.assocPath(['web_embed', 'frequency_type'], 'oneoff', embed)
              )
            }
            radio
            label="One time"
          />
          <Form.Checkbox
            checked={webEmbedSetting.web_embed.frequency_type === 'persistent'}
            onChange={() =>
              updateWebEmbedSetting((embed) =>
                R.assocPath(['web_embed', 'frequency_type'], 'persistent', embed)
              )
            }
            radio
            label="Persistent"
          />

          <FormField inline>
            <Checkbox
              checked={webEmbedSetting.web_embed.frequency_type === 'ongoing'}
              onChange={() =>
                updateWebEmbedSetting((embed) =>
                  R.assocPath(['web_embed', 'frequency_type'], 'ongoing', embed)
                )
              }
              radio
              label="Ongoing: send every "
            />
            <Dropdown
              disabled={webEmbedSetting.web_embed.frequency_type === 'ongoing' ? false : true}
              defaultValue={webEmbedSetting.web_embed.frequency_day}
              selection
              options={frequencyOptions}
              onChange={(e, data) =>
                updateWebEmbedSetting((embed) =>
                  R.assocPath(['web_embed', 'frequency_day'], data.value, embed)
                )
              }
              search
            />
          </FormField>
        </FormField>
        <FormField>
          <Grid stackable columns={3}>
            <Grid.Column>
              <h3>
                <TooltipLabel
                  label="Connected service"
                  descriptionTitle="Connected service"
                  descriptionContent="Connect this survey with an authenticated integration to enable ticket-level tracking. When a external ID (such as a ticket number or product SKU) is included in the web-embed survey code, Simplesat can sync feedback with your helpdesk, updating ticket details, adding private notes, and more based on your integration settings."
                />
              </h3>
              <Dropdown
                fluid
                placeholder="Select connected service"
                search
                selection
                clearable
                value={connectorId}
                onChange={(_, { value }) => {
                  setConnectorId(value)
                  setIsFormDirty(true)
                }}
                options={connectorOptions}
                loading={loadingConnectors || saveConnector.loading}
                disabled={loadingConnectors || saveConnector.loading}
              />
            </Grid.Column>
          </Grid>
        </FormField>

        <button type="submit" hidden ref={buttonRef} />
      </Form>
    </React.Fragment>
  )
}

function getAudienceRulesFields(advancedFilterFields) {
  return BASE_FIELDS.concat(
    advancedFilterFields
      .filter((field) => ['customer.attribute', 'ticket.attribute'].includes(field.key))
      .map((record) => {
        return {
          field: record.field,
          label: record.label,
          key: record.key,
          field_type: record.field_type,
        }
      })
  )
}

export function convertTicketAttributeFormat(audienceRule) {
  const audienceRuleFormatted = audienceRule.map((rule) => {
    const field = R.prop('extra', rule)
    if (field && R.equals('ticket.attribute', field.key)) {
      rule.field = 'ticket.custom_attributes.' + rule.field
    }
    return rule
  })

  return audienceRuleFormatted
}

const frequencyOptions = [
  {
    text: '1 week',
    value: '1_week',
  },
  {
    text: '2 week',
    value: '2_weeks',
  },
  {
    text: '1 month',
    value: '1_month',
  },
  {
    text: '2 months',
    value: '2_months',
  },
  {
    text: '3 months',
    value: '3_months',
  },
  {
    text: '6 months',
    value: '6_months',
  },
  {
    text: '12 months',
    value: '12_months',
  },
]

const BASE_FIELDS = [
  {
    key: 'name',
    label: 'Customer name',
    field: 'name',
    isCoreField: true,
  },
  {
    key: 'email',
    label: 'Customer email',
    field: 'email',
    isCoreField: true,
  },
  {
    key: 'customer.id',
    label: 'Customer Id',
    field: 'customer.id',
    isCoreField: false,
  },
  {
    key: 'company',
    label: 'Company',
    field: 'company',
    isCoreField: true,
  },
  {
    key: 'customer.tag',
    label: 'Tags',
    field: 'customer.tag',
    isCoreField: true,
  },
  {
    key: 'ticket.id',
    label: 'Ticket Id',
    field: 'ticket.id',
    isCoreField: false,
  },
]



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/WebEmbedSurveyPublish/index.js